import React from "react";
import {
  Box,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HPInput from "@/components/common/Input";
import { QuestionsList } from "@/components/modals/chart-modal/QuestionsList";
import type { DatasetQuestionsSearchResponse } from "@/models/Dataset";

interface SourceQuestionAccordionProps {
  label: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  selectedQuestionLabel?: string;
  selectedQuestionTitle?: string;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  eligibleQuestions: DatasetQuestionsSearchResponse[];
  selectedQuestionId: string;
  onQuestionSelect: (question: DatasetQuestionsSearchResponse) => void;
  isFetching: boolean;
  observerTarget: React.RefObject<HTMLDivElement | null>;
  noResultsMessage: string;
}

/** The searchable Source Question picker used by MetricRowBuilder — kept as
 *  its own component to keep MetricRowBuilder under the file-size limit. */
const SourceQuestionAccordion: React.FC<SourceQuestionAccordionProps> = ({
  label,
  expanded,
  onExpandedChange,
  selectedQuestionLabel,
  selectedQuestionTitle,
  searchTerm,
  onSearchTermChange,
  eligibleQuestions,
  selectedQuestionId,
  onQuestionSelect,
  isFetching,
  observerTarget,
  noResultsMessage,
}) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          {selectedQuestionLabel && (
            <Typography variant="body2" fontWeight={500}>
              {selectedQuestionLabel}
            </Typography>
          )}
          {selectedQuestionTitle && (
            <Typography variant="caption" color="text.secondary">
              {selectedQuestionTitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <HPInput
          label=""
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          placeholder="Search by label or title..."
        />
        <Box sx={{ mt: 1 }}>
          {!isFetching && eligibleQuestions.length === 0 ? (
            <Alert severity="info">{noResultsMessage}</Alert>
          ) : (
            <QuestionsList
              questions={eligibleQuestions}
              selectedQuestionId={selectedQuestionId}
              onQuestionSelect={onQuestionSelect}
              searchTerm={searchTerm}
              isFetching={isFetching}
              hasMore={false}
              observerTarget={observerTarget}
              compact
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SourceQuestionAccordion;
